import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Search, 
  Plus, 
  Download,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  Edit,
  Trash2,
  Receipt,
  FileText,
  File
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { generatePaymentsPdf } from '../utils/pdfGenerator';
import PaymentFormModal from './PaymentFormModal';
import PaymentReceiptModal from './PaymentReceiptModal';
import { default as membersData } from '../mock/members';
// Utilidad para exportar a Excel
function exportPaymentsExcel(data) {
  const getPaymentMethodLabel = (method) => {
    const labels = {
      wechat: 'WeChat Pay',
      alipay: 'Efectivo',
      bank: 'Transferencia Bancaria',
      cash: 'Efectivo'
    };
    return labels[method] || method;
  };
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.slice(0, 10);
  };
  const formatCLP = (amount) => {
    return Number(amount).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  // Calcular total filtrado
  const totalFiltrado = data.reduce((sum, p) => sum + Number(p.amount), 0);
  const wsData = [
    ['Socio', 'Monto', 'Fecha de Pago', 'Método', 'Estado', 'Tipo Membresía', 'Vencimiento'],
    ...data.map(p => [
      p.memberName,
      formatCLP(p.amount),
      formatShortDate(p.paymentDate),
      getPaymentMethodLabel(p.paymentMethod),
      p.status,
      p.membershipType,
      formatShortDate(p.dueDate)
    ]),
    [], // Fila vacía
    ['TOTAL FILTRADO:', formatCLP(totalFiltrado), '', '', '', '', '']
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pagos');
  XLSX.writeFile(wb, 'pagos.xlsx');
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - offset);
  return d.toISOString().slice(0, 10);
}

const Payments = () => {
  // Filtros de año y mes
  const [filterYear, setFilterYear] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);

  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
const [selectedPayment, setSelectedPayment] = useState(null);

const handleViewReceipt = (payment) => {
  if (payment.status !== 'completed') {
    alert('Solo los pagos completados pueden generar comprobante.');
    return;
  }
  setSelectedPayment(payment);
  setShowReceiptModal(true);
};
  // Cargar pagos desde la base de datos
  const fetchPayments = () => {
    fetch('https://chungwhav2.tnf.cl/api/pagos')
      .then(res => res.json())
      .then(data => setPayments(data));
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchMembers = () => {
  fetch('https://chungwhav2.tnf.cl/api/miembros')
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    fetchPayments();
    fetchMembers();
  }, []);

  const getStatusBadge = (status) => {
    const badges = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completado' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'Vencido' }
    };
    return badges[status] || badges.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getMembershipLabel = (type) => {
    const labels = {
      basic: 'Básica',
      premium: 'Premium',
      vip: 'VIP'
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      wechat: 'WeChat Pay',
      alipay: 'Efectivo',
      bank: 'Transferencia Bancaria'
    };
    return labels[method] || method;
  };

  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const filteredPayments = payments.filter(payment => {
    const member = members.find(m => m.id === payment.memberId);
    const rut = member?.rut || '';
    const name = payment.memberName || '';
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) || rut.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || payment.status === filterStatus;
    // Filtro por año
    let matchesYear = true;
    if (filterYear !== 'all' && payment.paymentDate) {
      matchesYear = payment.paymentDate.startsWith(filterYear);
    }
    // Filtro por mes
    let matchesMonth = true;
    if (filterMonth !== 'all' && payment.paymentDate) {
      const month = payment.paymentDate.split('-')[1];
      matchesMonth = month === filterMonth;
    }
    // Filtrar por usuario si el rol es 'user'
    if (currentUser.role === 'user') {
      return matchesSearch && matchesFilter && matchesYear && matchesMonth && member?.email === currentUser.email;
    }
    return matchesSearch && matchesFilter && matchesYear && matchesMonth;
  });
    // Definir paginación después del filtro
    const totalPages = Math.max(1, Math.ceil(filteredPayments.length / rowsPerPage));
    const paginatedPayments = filteredPayments.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Estadísticas solo del usuario logeado si es rol 'user'
  let userPayments = payments;
  if (currentUser.role === 'user') {
    const miembroActual = members.find(m => m.email === currentUser.email);
    userPayments = miembroActual ? payments.filter(p => p.memberId === miembroActual.id) : [];
  }
  const totalRevenue = userPayments.filter(p => p.status === 'completed').reduce((sum, p) => sum + Number(p.amount), 0);
  const pendingAmount = userPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + Number(p.amount), 0);
  const overdueAmount = userPayments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + Number(p.amount), 0);
  // Monto filtrado en tiempo real
  const filteredAmount = filteredPayments.reduce((sum, p) => sum + Number(p.amount), 0);
  // Pagos del mes actual
  const now = new Date();
  const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
  const currentYear = now.getFullYear();
  const paymentsThisMonth = userPayments.filter(p =>
    p.paymentDate &&
    p.paymentDate.startsWith(`${currentYear}-${currentMonth}`)
  ).length;

  const handleExportPdf = () => {
    generatePaymentsPdf(filteredPayments, filteredAmount);
  };

  const handleAddPayment = () => {
    setPaymentToEdit(null);
    setShowPaymentModal(true);
  };

  const handleEditPayment = (payment) => {
    setPaymentToEdit({
      ...payment,
      paymentDate: formatDate(payment.paymentDate),
      dueDate: formatDate(payment.dueDate)
    });
    setShowPaymentModal(true);
  };

  const handleDeletePayment = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este pago?')) {
  fetch(`https://chungwhav2.tnf.cl/api/pagos/${id}`, {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(() => fetchPayments());
    }
  };

  const handleSavePayment = (newPaymentData) => {
    const member = members.find(m => m.id === parseInt(newPaymentData.memberId));
    const paymentWithMemberName = { ...newPaymentData, memberName: member ? member.name : 'Desconocido' };

  fetch('https://chungwhav2.tnf.cl/api/pagos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentWithMemberName)
    })
      .then(res => res.json())
      .then(() => {
        setShowPaymentModal(false);
        fetchPayments();
      });
  };

  return (
  <div className="space-y-6 px-2 sm:px-4 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Gestión de Pagos de Membresía
          </h1>
          <p className="text-gray-600">Administre los pagos y facturas de los socios</p>
        </div>
        {currentUser.role !== 'user' && (
          <motion.button
            onClick={handleAddPayment}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            Registrar Pago
          </motion.button>
        )}
      </motion.div>

      {/* Payment Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { 
            title: 'Monto Filtrado', 
            value: `$${filteredAmount.toLocaleString()}`, 
            icon: DollarSign, 
            color: 'from-purple-500 to-indigo-500',
            bgColor: 'from-purple-50 to-indigo-50'
          },
          { 
            title: 'Ingresos Totales', 
            value: `$${totalRevenue.toLocaleString()}`, 
            icon: DollarSign, 
            color: 'from-green-500 to-emerald-500',
            bgColor: 'from-green-50 to-emerald-50'
          },
          { 
            title: 'Monto Pendiente', 
            value: `$${pendingAmount.toLocaleString()}`, 
            icon: Clock, 
            color: 'from-yellow-500 to-orange-500',
            bgColor: 'from-yellow-50 to-orange-50'
          },
          { 
            title: 'Monto Vencido', 
            value: `$${overdueAmount.toLocaleString()}`, 
            icon: XCircle, 
            color: 'from-red-500 to-pink-500',
            bgColor: 'from-red-50 to-pink-50'
          },
          { 
            title: 'Pagos del Mes', 
            value: paymentsThisMonth, 
            icon: Calendar, 
            color: 'from-blue-500 to-cyan-500',
            bgColor: 'from-blue-50 to-cyan-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} border border-gray-200/50 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.title}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filters and Search */}
    
      <motion.div
  className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o RUT de socio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>
          <div className="flex gap-3">
            {/* Filtro de año y mes con formato de estado */}
            <div className="flex items-center gap-2">
              <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 border border-gray-200 flex items-center">
                <span className="text-blue-800 font-semibold mr-2">Año:</span>
                <select value={filterYear} onChange={e => setFilterYear(e.target.value)} className="bg-blue-100 text-blue-800 rounded px-2 py-1 font-semibold focus:outline-none">
                  <option value="all">Todos</option>
                  {[...new Set(payments.map(p => p.paymentDate?.slice(0,4)).filter(Boolean))].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 border border-gray-200 flex items-center">
                <span className="text-cyan-800 font-semibold mr-2">Mes:</span>
                <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="bg-cyan-100 text-cyan-800 rounded px-2 py-1 font-semibold focus:outline-none">
                  <option value="all">Todos</option>
                  {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-gray-200 flex items-center">
              <span className="text-green-800 font-semibold mr-2">Estado:</span>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-green-100 text-green-800 rounded px-2 py-1 font-semibold focus:outline-none"
              >
                <option value="all">Todos</option>
                <option value="completed">Completado</option>
                <option value="pending">Pendiente</option>
                <option value="overdue">Vencido</option>
              </select>
            </div>
            <motion.button
              onClick={handleExportPdf}
              className="px-4 py-3 bg-red-100 hover:bg-red-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Exportar PDF"
            >
              <File className="w-5 h-5 text-red-600" />
            </motion.button>
            <motion.button
              onClick={() => exportPaymentsExcel(filteredPayments)}
              className="px-4 py-3 bg-green-100 hover:bg-green-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Exportar Excel"
            >
              <FileText className="w-5 h-5 text-green-600" />
            </motion.button>
          </div>
        </div>
      </motion.div>
  {/* Filtro de cantidad de resultados y paginación */}
      <div className="flex items-center gap-4 mb-2">
        <label className="text-sm text-gray-700">Mostrar:</label>
        <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} className="px-2 py-1 border rounded">
          {[10,20,50,100,500,1000].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={() => setPage(p => Math.max(p-1,0))} className="px-2 py-1 border rounded bg-gray-100 hover:bg-gray-200">&lt;</button>
        <span className="text-sm">Página {page+1} de {totalPages}</span>
        <button onClick={() => setPage(p => Math.min(p+1, totalPages-1))} className="px-2 py-1 border rounded bg-gray-100 hover:bg-gray-200">&gt;</button>
      </div>
      {/* Filtros y búsqueda */}
      {/* Payments Table */}
      <motion.div
  className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-x-auto overflow-y-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="min-w-[600px] w-full text-sm">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Socio</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo de Membresía</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Monto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Estado</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha de Pago</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha de Vencimiento</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Método de Pago</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedPayments.map((payment, index) => {
        {/* Pagination Controls */}
        <div className="flex justify-end items-center gap-2 p-4">
          <button
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50"
            onClick={() => setPage(p => Math.max(p-1,0))}
            disabled={page === 0}
          >
            ←
          </button>
          <span className="mx-2 text-sm">Página {page+1} de {totalPages}</span>
          <button
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50"
            onClick={() => setPage(p => Math.min(p+1, totalPages-1))}
            disabled={page === totalPages-1 || totalPages === 0}
          >
            →
          </button>
        </div>
                const statusConfig = getStatusBadge(payment.status);
                return (
                  <motion.tr
                    key={payment.id}
                    className="hover:bg-gray-50/50 transition-colors duration-200"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.05 }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {payment.memberName?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{payment.memberName}</p>
                          <p className="text-sm text-gray-500">RUT: {(() => {
                            const member = members.find(m => m.id === payment.memberId);
                            return member?.rut || '-';
                          })()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                        {getMembershipLabel(payment.membershipType)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-900">${Number(payment.amount).toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text} flex items-center gap-1`}>
                          {getStatusIcon(payment.status)}
                          {statusConfig.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(payment.dueDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {getPaymentMethodLabel(payment.paymentMethod)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {payment.status === 'completed' && (
                          <button
                            onClick={() => handleViewReceipt(payment)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                            title="Ver comprobante"
                          >
                            <Receipt className="w-4 h-4" />
                          </button>
                        )}
                        {currentUser.role !== 'user' && (
                          <>
                            <motion.button
                              onClick={() => handleEditPayment(payment)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Edit className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </motion.button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>

      <PaymentFormModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSave={handleSavePayment}
        paymentToEdit={paymentToEdit}
        members={members}
      />

      <PaymentReceiptModal
  isOpen={showReceiptModal}
  onClose={() => setShowReceiptModal(false)}
  payment={selectedPayment}
/>
    </div>
  );
};

export default Payments;